import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Trash2, Minus, Plus, Heart } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { items, wishlist, remove, updateQty, subtotal, clear, toggleWish, add } = useCart();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = () => {
    if (!email || !name) { toast.error("Please enter your name and email"); return; }
    if (items.length === 0) return;
    // Persist buyer info to localStorage for the bank-details page reference
    localStorage.setItem("aurea_buyer", JSON.stringify({ name, email, phone }));
    navigate("/payment/bank-details");
  };

  return (
    <div className="pt-32 pb-24 container-x" data-testid="cart-page">
      <div className="kicker mb-3">Cart</div>
      <h1 className="font-display text-4xl md:text-6xl mb-10">Your <em className="not-italic gold-text">Selection.</em></h1>

      {items.length === 0 ? (
        <div className="glass rounded-3xl py-20 text-center">
          <p className="text-white/50 mb-6">Your cart is empty.</p>
          <Link to="/shop" className="btn-gold">Browse Shop</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_400px] gap-10">
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.key} className="glass rounded-2xl p-4 flex items-center gap-5" data-testid={`cart-item-${i.key}`}>
                <img src={i.image} alt={i.name} className="w-24 h-24 rounded-xl object-cover" />
                <div className="flex-1">
                  <div className="font-display text-lg">{i.name}</div>
                  {i.color && (
                    <div className="text-xs text-white/60 mt-1 inline-flex items-center gap-1.5" data-testid={`cart-color-${i.key}`}>
                      <span className="w-3 h-3 rounded-full inline-block" style={{ background: i.color_hex, boxShadow: "inset 0 0 0 1px rgba(15,12,8,0.2)" }}></span>
                      Colour: {i.color}
                    </div>
                  )}
                  <div className="text-sm text-white/50 mt-1">₹{i.price.toLocaleString('en-IN')}</div>
                </div>
                <div className="glass rounded-full flex items-center">
                  <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-3 py-1.5" data-testid={`cart-minus-${i.key}`}><Minus className="w-3 h-3" /></button>
                  <span className="px-2 text-sm">{i.quantity}</span>
                  <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-3 py-1.5" data-testid={`cart-plus-${i.key}`}><Plus className="w-3 h-3" /></button>
                </div>
                <div className="font-medium w-24 text-right">₹{(i.price * i.quantity).toLocaleString('en-IN')}</div>
                <button onClick={() => remove(i.key)} className="text-white/40 hover:text-red-400" data-testid={`cart-remove-${i.key}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={clear} className="text-xs text-white/40 hover:text-red-400 mt-4">Clear cart</button>
          </div>
          <div className="glass rounded-2xl p-8 h-fit sticky top-28" data-testid="checkout-panel">
            <h3 className="font-display text-2xl mb-6">Order Summary</h3>
            <div className="space-y-3 text-sm text-white/70">
              <div className="flex justify-between"><span>Subtotal (incl. GST)</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-xs text-white/50 pl-4"><span>· GST @ 18% (included)</span><span>₹{Math.round(subtotal - subtotal / 1.18).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-xs text-white/50 pl-4"><span>· Base price</span><span>₹{Math.round(subtotal / 1.18).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span className="text-gold">Free · India-wide</span></div>
            </div>
            <div className="hairline my-6"></div>
            <div className="flex justify-between text-xl font-display mb-2">
              <span>Total</span><span data-testid="cart-total">₹{subtotal.toLocaleString('en-IN')}</span>
            </div>
            <div className="text-[10px] text-white/40 mb-6 leading-relaxed">All prices are inclusive of 18% GST. A GST invoice with your name / company details will be issued at delivery.</div>
            <div className="space-y-3 mb-6">
              <input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="checkout-name" />
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email" />
              <input type="tel" placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone" />
            </div>
            <button onClick={checkout} disabled={loading} className="btn-gold w-full justify-center" data-testid="checkout-btn">
              Proceed to Bank Transfer
            </button>
            <p className="text-[10px] text-center text-white/40 mt-4">Direct bank transfer · NEFT / IMPS / RTGS / UPI</p>
          </div>
        </div>
      )}

      {wishlist.length > 0 && (
        <div className="mt-24">
          <h2 className="font-display text-3xl mb-8 flex items-center gap-3"><Heart className="w-6 h-6 text-gold" /> Wishlist</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {wishlist.map((w) => (
              <div key={w.id} className="card-luxury p-4" data-testid={`wish-item-${w.id}`}>
                <img src={w.image} alt={w.name} className="w-full aspect-[4/5] object-cover rounded-xl" />
                <div className="mt-4 font-display text-lg">{w.name}</div>
                <div className="text-sm text-gold mt-1">₹{w.price.toLocaleString('en-IN')}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => (w.has_colors ? navigate(`/product/${w.id}`) : add(w))} className="btn-gold text-xs flex-1 justify-center px-3 py-2" data-testid={`wish-add-${w.id}`}>{w.has_colors ? "Choose colour" : "Add to Cart"}</button>
                  <button onClick={() => toggleWish(w)} className="btn-ghost-outline text-xs px-3 py-2"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
